import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { userServiceToken, UserService } from 'go-modules/models/user/user.service';
import { UserService as NgxUserService } from '@go-modules/services/user/user.service';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';
import type { EmailFrequencyMatOption, NotificationSettings } from '@go-modules/interfaces/notification-settings';
import { NotificationFrequencies } from '@go-modules/interfaces/notification-settings';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';

export const DEBOUNCE_TIME = 500;

@Component({
	selector: 'email-notifications',
	templateUrl: './email-notifications.component.html',
	styleUrls: ['./email-notifications.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EmailNotificationsComponent implements OnInit{
	public form: UntypedFormGroup;
	public user;
	public shouldShowSaveButton$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public frequencies: EmailFrequencyMatOption[];
	public frequenciesNotWeekly: EmailFrequencyMatOption[];
	public frequenciesNotImmediately: EmailFrequencyMatOption[];
	public frequenciesNotHourly: EmailFrequencyMatOption[];
	public assignment_submitted_email_frequency: EmailFrequencyMatOption;
	public feedback_submitted_email_frequency: EmailFrequencyMatOption;
	public new_assignment_email_frequency: EmailFrequencyMatOption;
	public assignment_due_email_frequency: EmailFrequencyMatOption;
	public assignment_graded_email_frequency: EmailFrequencyMatOption;
	public emailFrequencies: string[] = [
		'assignment_submitted_email_frequency',
		'feedback_submitted_email_frequency',
		'new_assignment_email_frequency',
		'assignment_due_email_frequency',
		'assignment_graded_email_frequency'
	];
	public notificationSettings: NotificationSettings;
	public environmentVarsService: EnvironmentVarsService;

	constructor (
		private fb: UntypedFormBuilder,
		private ngxUserService: NgxUserService,
		private ngxGoToastService: NgxGoToastService,
		@Inject(userServiceToken) private userService: UserService
	) {}

	public ngOnInit () {
		this.environmentVarsService = EnvironmentVarsService.getInstance();

		this.user = this.userService.currentUser;
		this.frequencies = this.getFrequencies();
		this.frequenciesNotImmediately = this.frequencies.filter((frequency) =>
			frequency.name !== NotificationFrequencies.IMMEDIATELY
		);
		this.frequenciesNotWeekly = this.frequenciesNotImmediately.filter((frequency) =>
			frequency.name !== NotificationFrequencies.WEEKLY
		);
		this.frequenciesNotHourly = this.frequencies.filter((frequency) =>
			frequency.name !== NotificationFrequencies.HOURLY
		);
		this.initializeForm();

		this.ngxUserService.getNotificationSettings(this.user.user_id).subscribe((notificationSettings) => {
			this.notificationSettings = notificationSettings;
			this.emailFrequencies.forEach((emailFrequency) => {
				this[emailFrequency] = this.frequencies.find(({value}) =>
					value === notificationSettings[emailFrequency]
				);

				this.form.get(emailFrequency).setValue(this[emailFrequency]);
			});
		});
	}

	public hasReviewerRoleOrHigher (): boolean  {
		return this.userService.currentUser.isReviewerPlus;
	}

	public hasPresenterRole (): boolean {
		return this.userService.currentUser.isPresenter;
	}

	public isLti () {
		return this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public getFrequencies () {
		return [
			{ name: NotificationFrequencies.IMMEDIATELY, value: NotificationFrequencies.IMMEDIATELY.toLowerCase() },
			{ name: NotificationFrequencies.HOURLY, value: NotificationFrequencies.HOURLY.toLowerCase() },
			{ name: NotificationFrequencies.DAILY, value: NotificationFrequencies.DAILY.toLowerCase() },
			{ name: NotificationFrequencies.WEEKLY, value: NotificationFrequencies.WEEKLY.toLowerCase() },
			{ name: NotificationFrequencies.NEVER, value: NotificationFrequencies.NEVER.toLowerCase() }
		];
	}

	private initializeForm () {
		const group = {};

		this.emailFrequencies.forEach((emailFrequency) =>
			group[emailFrequency] = new UntypedFormControl(this[emailFrequency], [Validators.required])
		);

		this.form = this.fb.group(group);

		this.form.valueChanges.pipe(
			debounceTime(DEBOUNCE_TIME),
			distinctUntilChanged()
		).subscribe((val) => {
			const hasChanges = this.emailFrequencies.some((emailFrequency) =>
				this[emailFrequency] !== val[emailFrequency]
			);

			this.shouldShowSaveButton$.next(hasChanges);
		});
	}

	public saveNotificationSettings (form) {
		if (!form.valid) {
			return;
		}

		this.emailFrequencies.forEach((emailFrequency) => {
			this.notificationSettings[emailFrequency] = this.form.get(emailFrequency).value.value;
		});

		this.ngxUserService.saveNotificationSettings(this.user.user_id, this.notificationSettings).subscribe(() => {
			this.shouldShowSaveButton$.next(false);
			this.ngxGoToastService.createToast({
				type: GoToastStatusType.SUCCESS,
				message: 'common-save-success'
			});
		});
	}
}
